<template>
	<Transition name="slide">
		<div
			v-show="visible"
			class="callout flex flex--sb gap-3 mb-3"
			:class="[
				calloutClass,
				'callout--' + size,
				{'callout--title': title},
				{'callout--attached': attached},
				{'callout--dismissable': dismissable},
				{'callout--has-icon': useIcon}
			]"
		>
			<div class="w-100">
				<h1 v-if="title" class="callout__title mt-0 mb-2 font-bold">
					<Inline v-if="getIcon && useIcon" :svg="getIcon" class="callout__icon" /> {{ title }}
				</h1>
				<div class="callout__content flex gap-3">
					<Inline v-if="getIcon && !title && useIcon" :svg="getIcon" class="callout__icon" />
					<div
						class="content"
						:class="$slots.button ? 'callout__button-container' : 'callout__inner-container'"
					>
						<div :class="customSlotClass"><slot /></div>
						<div class="callout__button-inner-container"><slot name="button" /></div>
					</div>
				</div>
			</div>
			<button v-if="dismissable" class="button--no-style" :style="exStyle" aria-label="dismiss" @click="handleDismiss()">
				<Inline svg="ex" />
			</button>
		</div>
	</Transition>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import { defineComponent } from 'vue';

	type Size = 's' | 'm' | 'l';
	type ExPosition = 'top' | 'center';
	export type CalloutType = 'error' | 'info' | 'success' | 'warning' | 'plain' | 'tip' | 'support';

	export default defineComponent({
		name: 'CCallout',
		props: {
			type: {
				default: null,
				type: String as PropType<CalloutType>,
				validator: function (value: string) {
					return ['error', 'info', 'success', 'warning', 'plain', 'tip', 'support'].indexOf(value) !== -1;
				}
			},
			title: {
				type: String,
				default: null
			},
			icon: {
				type: String,
				default: null
			},
			size: {
				default: 'm',
				type: String as PropType<Size>
			},
			attached: {
				default: false,
				type: Boolean
			},
			dismissable: {
				default: false,
				type: Boolean
			},
			notificationId: {
				default: undefined,
				type: String
			},
			useIcon: {
				default: true,
				type: Boolean
			},
			exPosition: {
				default: null,
				type: String as PropType<ExPosition>
			},
			customSlotClass: {
				default: null,
				type: String
			}
		},
		emits: ['change'],
		data: () => {
			return {
				visible: true
			};
		},
		computed: {
			getIcon() {
				return !this.icon && ['error', 'info', 'success', 'warning'].includes(this.type) ? this.type : this.icon ? this.icon : null;
			},
			calloutClass() {
				return this.type ? 'callout--' + this.type : null;
			},
			exStyle() {
				if (!this.exPosition)
					return;

				const positionFlexMap = { top: 'start', center: 'center' };

				return {
					display: 'flex',
					'align-items': positionFlexMap[this.exPosition]
				};
			}
		},
		methods: {
			handleDismiss() {
				this.visible = false;
				this.$emit('dismiss', this.notificationId);
			}
		}
	});
</script>

<style lang="scss">
.callout {
	background: var(--shade-3);

	&--info {
		@include callout--info;
	}

	&--warning {
		@include callout--warning;
	}

	&--error {
		@include callout--error;

		&.callout--s {
			color: var(--text-error);
		}

		.button--primary {
			background: var(--red-4) !important;
			color: var(--red-1) !important;

			&:hover {
				border-color: var(--bg-error);
			}
		}
	}

	&--success {
		@include callout--success;
	}

	&--attached {
		border-top-width: 0;
		margin-top: 0 !important;
	}

	:root &--has-icon {

		.list-style {
			// Need to reduce padding due to the callout already being indented
			padding-left: 20px;
		}
	}

	:where(html:not(.website)) & {
		padding: $space__4;

		& + .callout {
			margin-top: $space__4;
		}

		&__inner-container {
			min-width: 0;
		}

		&__button-container {
			display: flex;
			align-items: center;
			justify-content: space-between;
			width: 100%;
			gap: $space__4;

			@include b-down($b5) {
				align-items: flex-start;
				flex-direction: column;
			}

			// Negative margins on the button so that it cuts into the callout margins does not increase the height
			// of the callout causing an awkward space below the text. The button shouldn't cut into the right margin
			// if there is a dismiss button (see below).
			.callout__button-inner-container {
				flex-shrink: 0;
				margin: -5px;
				font-size: $size__1;
				white-space: nowrap;
			}
		}

		&--dismissable .callout__button-container {

			a,
			button {
				margin-right: 0;
			}
		}

		&--m {

			.callout__icon {
				width: $size__icon--large;
				height: $size__icon--large;
			}
		}

		&--s {
			padding: $space__3;

			.callout__icon {
				width: $size__icon;
				height: $size__icon;
				margin-top: 1px;
			}
		}

		h1,
		h2,
		h3,
		h4,
		h5,
		h6 {
			font-family: $app-text-type;
			font-size: $size__3;
			font-weight: $weight__text--medium;
		}

		:root &__content {

			*:first-child {
				font-size: $size__2;
			}
		}

		&--title.callout--has-icon {
			padding-left: $space__4 + $size__icon--large + $space__icon;
		}

		&__title {
			position: relative;
			font-size: $size__5;

			.callout__icon {
				position: absolute;
				left: ( $size__icon--large + $space__icon) * -1;
			}
		}

		&__icon {
			vertical-align: middle;
		}
	}

	html.website .wiki & {

		p,
		li,
		table {
			font-size: modular-scale(3);
		}
	}

	:where(html.website) & {
		padding: $gutter*.75;

		&--s {
			padding: $gutter*.25;
			font-size: modular-scale(2);
		}

		&--l {
			font-size: modular-scale(4);
		}

		&__content {
			align-items: flex-start;
		}

		&__icon {
			width: 1.4em;
		}

		&__inner-container :first-of-type {
			margin-top: 0;
		}
	}
}
</style>